import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { FeaturesReady } from '@growthbook/growthbook-react';
import { clearBalanceData, handleBalanceData } from 'features/customerPanel/store/cpPanel.action';
import dataLayerPaymentManager from 'features/deposit/dataLayerPaymentManager';
import { loginUser, showLoginModal, showVerificationModal } from 'features/login/store/login.action';
import { fetchBonusesAndFreebets } from 'features/offers/store/offers.action';
import { useTranslation } from 'react-i18next';
import { APP_URL, KEEP_ALIVE_API_INTERVAL } from 'shared/constants/common';
import useInterval from 'shared/hooks/useInterval';
import { useRecaptcha } from 'shared/hooks/useRecaptcha';
import { calculateTax } from 'shared/store/coupon.action';
import {
	createAction,
	createSessionCookie,
	decryptData,
	eraseSessionCookie,
	getBalanceDetails,
	getUserDetails,
	isLoggedin,
	isMobile,
	logout,
	showModalForUnverifiedAccount,
	updateCookie,
} from 'shared/util/utility';
import { CustomerApi, LoginApi } from '../../api';
import Logo from '../../assets/images/logo.svg';
import { useAutoLoginImageUrlFeature } from '../featureFlags';
import { CouponState, State } from '../interface';
import { reactNativeEvent } from '../react-native/event';
import * as actionTypes from '../store/action-types';

const SessionExpired: React.FC = () => {
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();
	const history = useHistory();
	const coupon = useSelector<State, CouponState>((state) => state.coupon);
	const { t } = useTranslation();
	const userData = useSelector((state: RootStateOrAny) => state.login);
	const hash = uuidv4(); // np. '1b4e28ba-2fa1-11d2-883f-0016d3cca427'
	const { execute: executeRecaptcha } = useRecaptcha();

	const keepSession = async () => {
		try {
			const response = await LoginApi.keepAlive(isLoggedin());
			if (response) {
				if (response.headers && response.headers['x-odds-session']) {
					createSessionCookie(response.headers['x-odds-session']);
				}
				if (response.status === 200 && !response.data.isError && response.data.data && response.data.data.customerData && isLoggedin()) {
					const customerData = response.data.data.customerData;
					const currentBalanceData = getBalanceDetails();
					const balanceData = {
						...currentBalanceData,
						mainBalance: customerData.balance,
						bonusBalance: customerData.calculatedBonus,
						bonusBalanceList: customerData.bonusBalanceList,
						depositCounter: customerData.depositCounter,
						depositInfo: customerData.depositInfo,
					};

					dispatch(handleBalanceData(balanceData));
					updateCookie('x-odds-session');
					reactNativeEvent().send('LOGIN', { id: userData?.userDetails?.userId });
					if (customerData.balance > currentBalanceData.mainBalance) {
						CustomerApi.getCustomerAndAccountData().then((response: any) => {
							dataLayerPaymentManager.onPaymentEnds(customerData.balance, {
								payment_method: response.data.data.playersProfile.lastDepositMethod,
							});
						});
					}
				}
				if (response && response.data && response.data.isError && isLoggedin()) {
					setLoading(true);
					const logoutResp = await logout();
					if (logoutResp && logoutResp.headers && logoutResp.headers['x-odds-session']) eraseSessionCookie();
					dispatch(clearBalanceData());
					const status = await backgroundLogin();
					setLoading(false);
					if (!status) {
						if (!isMobile()) {
							dispatch(showLoginModal());
						} else {
							history.push(APP_URL.login, window.location.pathname);
						}
					} else {
						dispatch(createAction(actionTypes.BACKGROUND_LOGIN_STATUS, hash));
						const userDetails = getUserDetails();
						if (showModalForUnverifiedAccount(userDetails)) {
							dispatch(showVerificationModal());
						}
					}
				}
			}
		} catch (err) {
			console.error(err);
		}
	};

	const backgroundLogin = async () =>
		new Promise<boolean>(async (resolve) => {
			const token = await executeRecaptcha('LOGIN');

			try {
				const signInData = decryptData(localStorage.getItem('keepSignedIn'));
				const loginResponse = await (dispatch(
					loginUser(
						{
							login: signInData.login,
							password: signInData.password,
							keepSignedIn: signInData.keepSignedIn || false,
							recaptchaToken: token,
						},
						coupon,
					),
				) as unknown as Promise<{ isError: boolean }>);
				resolve(!loginResponse.isError);
			} catch (error) {
				resolve(false);
			}
		});

	const keepSessionDebounced = debounce(keepSession, 1000, { leading: true, trailing: false });

	useEffect(() => {
		dispatch(calculateTax());
		if (isLoggedin()) {
			keepSessionDebounced();
			dispatch(fetchBonusesAndFreebets());
		}

		const BannerCount = localStorage.getItem('bannerCount');

		if (!BannerCount) {
			localStorage.setItem('bannerCount', '0');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	useInterval(() => {
		if (isLoggedin()) {
			keepSessionDebounced();
		}
	}, KEEP_ALIVE_API_INTERVAL);

	const handleVisibilityChange = useCallback(() => {
		if (document.visibilityState === 'visible') {
			keepSessionDebounced();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		window.addEventListener('focus', handleVisibilityChange);
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
			window.removeEventListener('focus', handleVisibilityChange);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [handleVisibilityChange]);

	const defaultImage = <img className="animate-pulse" src={Logo} alt="Zakłady bukmacherskie" height={100} width={100} />;

	const autoLoginImageUrlFeature = useAutoLoginImageUrlFeature();

	const [imageStatus, setImageStatus] = useState<null | 'SUCCESS' | 'ERROR'>(null);

	return loading ? (
		<div className="backdrop-blur-sm fixed bg-dark-100/85 inset-0 z-overlay w-full h-full flex items-center justify-center overflow-auto">
			<div className="flex gap-4 flex-col items-center justify-center p-4 max-w-[360px]">
				{/* If the image URL is overwritten in Growthbook, replace the logo with the provided image and add the additional text */}
				{/* If the image cannot be loaded, fall back to the default image. */}
				<FeaturesReady timeout={500} fallback={defaultImage}>
					{autoLoginImageUrlFeature.value && imageStatus !== 'ERROR' ? (
						<>
							<img
								src={autoLoginImageUrlFeature.value}
								alt="Zakłady bukmacherskie"
								className="w-full"
								onLoad={() => setImageStatus('SUCCESS')}
								onError={() => setImageStatus('ERROR')}
							/>
							{t('backgroundLoginAdditionalText') && imageStatus === 'SUCCESS' && (
								<div className="font-bold text-gray-100 text-center">{t('backgroundLoginAdditionalText')}</div>
							)}
						</>
					) : (
						defaultImage
					)}
				</FeaturesReady>

				<div className="font-bold text-gray-100 text-center">{t('backgroundLoginText')}</div>
			</div>
		</div>
	) : null;
};

export default SessionExpired;
